// Versioning: x.y.z
//   x (major) — only increment when the user explicitly says to.
//   y (minor) — new feature or behavioural change.
//   z (patch) — bug fix only.
//
// Add a new entry at the top for every release (any change shipped to the
// portal). CURRENT_VERSION always reflects the newest entry.

export interface ChangelogSubEntry {
  title: string;
  items: string[];
}

export interface ChangelogEntry {
  version: string;
  date: string;
  entries: ChangelogSubEntry[];
}

export const CHANGELOG: ChangelogEntry[] = [
  {
    version: '2.0.0',
    date: '2026-09-15',
    entries: [
      {
        title: 'Investment Management',
        items: [
          'Added full governance: maker-checker approval on disposal, write-off and restructure; a change-log audit trail; central document storage for contracts, leases and valuations; and auto-generated exception flags (covenant breach, NPL, equity/offshore concentration, idle cash, lease/insurance expiry, allocation breach) surfaced together on the Dashboard.',
          'Added asset-class depth across all six classes: loan repayment schedules with interest-then-principal allocation and NPL/coverage-ratio tracking; bond coupon schedules with YTM, duration and amortised cost; property rent-schedule generation that can bill the tenant directly, plus lease/insurance expiry alerts; equity corporate actions, dividend tracking, NAV and total return; cash rollover, accrued interest and a portfolio liquidity ratio; and offshore FX movement and country concentration.',
          'Added a monthly allocation snapshot with a trend chart and a what-if reallocation calculator, an Income vs Expected ageing report, and a dedicated report per asset class (Loan Portfolio, Fixed Interest Maturity Profile, Property Lease Register, Cash & Liquidity, Equity Concentration, Offshore Exposure).',
        ],
      },
      {
        title: 'Finance & Accounting',
        items: [
          'Added a Budget & Cost Centre module: annual budgets by cost centre and GL account, re-appropriations and supplementaries with dual-control approval, and Committed Budget / Budget Variance reports. Purchase requests now check spend against the relevant budget line.',
          'Added a Banking module: bank accounts linked to the General Ledger, automated statement reconciliation against deposits and GL/journal activity, and deposit-slip recording.',
          'Journal entries can now be reversed (creating a linked, dual-control reversing draft), and a Contra Entry action was added for transfers between the Fund\'s own accounts.',
          'Added multi-currency support to AR/AP invoices, receipts and payment vouchers, and generalised cheques to cover AP payments (not just benefit claims), with a new EFT export for payment vouchers.',
          'Added a GL Account Ledger, Accounts Receivable and Accounts Payable ageing reports, an Asset Movement Log, and Spend Analysis / Vendor Performance reports for Procurement.',
          'Added formal RFQ issuance and structured vendor-response capture on purchase requests, alongside the existing quotation upload.',
          'Added a Ratio Analysis report (liquidity, solvency and fund-health ratios) and a Comprehensive Income statement to Consolidated reports.',
          'Fixed receipt PDF downloads failing.',
        ],
      },
      {
        title: 'Resources',
        items: [
          'Assets now show a status — Active, Pending Disposal, Impaired or Disposed. Disposing of a fixed asset is now a request that emails every Resources Administrator to approve, and the requesting officer can never also approve it.',
          'Assets can now have document attachments, such as photos, warranty cards and invoices.',
          'Added a fair-value revaluation action for assets, alongside the existing impairment and disposal actions.',
        ],
      },
      {
        title: 'Human Resources',
        items: [
          'The audit log now also covers Employee records (including bank/payout details) and User accounts, viewable under System > Audit Log.',
          'Attendance can now be bulk-imported from a CSV export of a biometric, swipe or door-access system, and every record carries a work-location (office, WFH or field) flag.',
          'The VNPF contribution now posts as its own line on the payslip. Added a Turnover / Attrition report.',
        ],
      },
      {
        title: 'System',
        items: ['Org Units gained an "Export Chart PDF" button, rendering the org hierarchy as a connected chart.'],
      },
    ],
  },
  {
    version: '1.35.0',
    date: '2026-09-02',
    entries: [
      {
        title: 'Dashboard',
        items: [
          'The old Dashboard (announcements and calendar) is now called Home.',
          'Added a new Dashboard — a cross-module KPI overview: members, active employers, contributions collected, benefits disbursed, penalties outstanding, investment portfolio value, staff headcount, revenue collected, GL reconciliation variance, and open purchase requests, all as current snapshot figures. Controlled by a new Dashboard KPIs Viewer role.',
        ],
      },
    ],
  },
  {
    version: '1.34.0',
    date: '2026-09-02',
    entries: [
      {
        title: 'Reports',
        items: [
          'Added a Custom category with a Report Packs builder — combine any set of existing reports into one downloadable PDF, e.g. for a monthly board meeting. Save a pack once and regenerate it anytime with a new date range.',
          'A pack only ever shows and generates the reports you personally have permission to view — a shared pack never reveals more than each viewer is independently entitled to see.',
        ],
      },
    ],
  },
  {
    version: '1.33.0',
    date: '2026-09-02',
    entries: [
      {
        title: 'Reports',
        items: [
          'Added a Consolidated reporting category with four standard financial statements built from the Chart of Accounts and posted GL activity: Trial Balance, Income Statement, Balance Sheet, and Statement of Changes in Fund Balance.',
          'Access is controlled by two new standalone permissions and a new Consolidated Financial Reports Viewer role — not bundled into any existing module, since these statements show fund-wide totals.',
        ],
      },
    ],
  },
  {
    version: '1.32.0',
    date: '2026-09-02',
    entries: [
      {
        title: 'Memberships',
        items: [
          'Merged Benefit Payments into Memberships — Claims, Approval Levels, Payment Batches, and Cheques now live under the Memberships menu; Benefit Payments no longer exists as a separate module.',
          'Existing benefit-claims roles keep working: every currently-assigned Benefits role and permission was renamed in place (e.g. Benefits Administrator is now Memberships Benefits Administrator), so no one loses access and every in-progress claim\'s approval chain is unaffected.',
        ],
      },
    ],
  },
  {
    version: '1.31.0',
    date: '2026-09-02',
    entries: [
      {
        title: 'Reports',
        items: [
          'Added a report for every remaining feature so each module\'s menu has matching coverage in Reports: Employer Register and Contribution Schedules Register (Memberships); Benefit Claims Register, Benefit Payment Batches Register, and Benefit Cheque Register (Benefit Payments); Customer Register (Revenue); Supplier Register (Procurement); Bookings Register (Resources); Correcting Journals Register (General Ledger Interface); and Employee Register and Positions / Establishment Register (Human Resources).',
          'The Resources reports (Asset Register, Facility Directory, Inventory Stock Levels, Inventory Movement Log) are now visible in the Reports menu — they existed already but were left off the menu.',
        ],
      },
    ],
  },
  {
    version: '1.30.0',
    date: '2026-09-01',
    entries: [
      {
        title: 'General Ledger Interface',
        items: [
          'Added an automated General Ledger Interface — every night, the system extracts finalised contributions, penalty payments, and benefit disbursements into a balanced GL batch, ready to hand to Finance.',
          'Each transaction type maps to a configurable chart-of-accounts code under System > GL Account Mappings; a type is skipped until Finance activates it with a real account.',
          'A batch is a downloadable CSV rather than a live transmission. Once exported, individual lines can be marked rejected (with a reason) if the accounting system bounces them, and requeued to re-extract on the next run — never automatically resubmitted.',
          'Added a Daily Interface Log, an Exceptions (rejected postings) report by age, a Monthly Sub-ledger to GL Reconciliation report, and a GL Account Mapping report.',
          'Added a fully separate Correcting Journal Entries screen for Finance-authored manual corrections — any number of debit/credit lines, must balance to post, and the officer who drafted an entry can never also be the one who finalizes it.',
          'Added a configurable cash/bank GL account and document prefixes for GL batches and journal entries under System > Settings.',
        ],
      },
    ],
  },
  {
    version: '1.29.0',
    date: '2026-09-01',
    entries: [
      {
        title: 'Benefit Payments',
        items: [
          'Added a new Benefit Payments module — officers capture retirement, withdrawal, death, and employer-sponsored benefit claims for a member, with entitlement calculated automatically from their contribution ledger balance.',
          'Claims pass through a configurable, admin-editable multi-level approval chain gated by amount (System > Benefit Approval Levels); the officer who assessed a claim can never also give its final approval.',
          "The instant a claim clears final approval, the system generates the payment instruction automatically: an EFT batch line for a member paid by bank transfer, or a numbered cheque for a member paid by cheque — no separate manual step.",
          'Cheques above a configurable amount require two distinct signatures; each signature re-confirms the signatory\'s own account password (and 2FA, if enabled) plus the claim reference — nobody who assessed or approved the claim can also sign or issue its cheque.',
          'Added EFT payment batches (downloadable CSV, mark disbursed) and a cheque register (sign, void, mark issued, download PDF).',
          'Added claim reversal (dual-control, senior-approver decision) and return-to-suspense/reissue for unclaimed or returned payments — reissuing does not require re-approval.',
          'Added Bank & Payout Details and a Beneficiaries sub-table to the Member detail page.',
          'Added a Benefit Claims in Progress by Stage report.',
          'Added configurable minimum retirement age, minimum membership period, cheque dual-signature threshold, GL accounts, and document prefixes under System > Settings.',
        ],
      },
    ],
  },
  {
    version: '1.28.0',
    date: '2026-09-01',
    entries: [
      {
        title: 'Memberships',
        items: [
          "Added Penalties & Surcharges to Memberships — automatic penalty assessment for overdue contribution schedules (a new daily scheduled check), plus manual ad hoc penalties with a reason code and supporting reference.",
          'Employers can apply for a penalty waiver; staff decide it under a dual-control rule (the officer who assessed a penalty can\'t also decide its waiver) with a configurable escalation threshold for larger waivers requiring senior/Manager-Board approval.',
          'Penalties are receipted separately from contributions, to a distinct GL income account — never credited to a member\'s own contribution ledger.',
          'Added a Penalty Ageing Schedule report (current/1-30/31-60/61-90/90+ days overdue).',
          'The Employer self-service role now also covers viewing and requesting waivers on their own penalties.',
          'Added configurable penalty rate, grace period, payment terms, waiver threshold, notice delivery method, and GL account settings under System > Settings.',
        ],
      },
    ],
  },
  {
    version: '1.27.0',
    date: '2026-08-31',
    entries: [
      {
        title: 'Memberships',
        items: [
          'Added Contribution Schedules to Memberships — employers submit a schedule of members and wages for a period, staff validate and record payment (including partial payments), and fully paid schedules post straight to each member\'s contribution ledger with a receipt number.',
          'CSV import for contribution schedule lines, matching members by email.',
          'A new self-service Employer role lets an employer log in to view and submit their own contribution schedules (no online payment — recording payment stays staff-only).',
          'The Employers list and detail page now show each employer\'s latest contribution schedule and its status.',
          'Added configurable minimum employee/employer contribution rates and a contribution-receipt number prefix under System > Settings.',
        ],
      },
    ],
  },
  {
    version: '1.26.0',
    date: '2026-08-31',
    entries: [
      {
        title: 'Memberships',
        items: [
          'Added Employers to Memberships — registration, TIN, industry sector, bank details, contribution frequency and status, plus a registration-certificate attachment.',
          'A Member’s Employment History (which employers they’ve worked under, and since when) is now tracked and shown on both the Member and Employer detail pages, with an "Assign employer" action.',
          'Added an Industry Sectors list to System > Lists.',
        ],
      },
    ],
  },
  {
    version: '1.25.0',
    date: '2026-08-30',
    entries: [
      {
        title: 'System',
        items: [
          'Restored the Resources sub-section (Assets, Facilities, Bookings, Inventory) under the Finance & Accounting menu, and the Resources Administrator/Operator/Viewer roles.',
        ],
      },
    ],
  },
  {
    version: '1.24.0',
    date: '2026-08-29',
    entries: [
      {
        title: 'System',
        items: [
          'Added an Identity Types list (National ID, Drivers License) and a new Identities tab on System > Users and Profile > Account for recording identity documents (type, number, issuer, expiry).',
          'Seeded realistic starter data for Positions, Pay Grades, Work Shifts, and the Departments and Position Status lists.',
        ],
      },
    ],
  },
  {
    version: '1.23.0',
    date: '2026-08-29',
    entries: [
      {
        title: 'Human Resources',
        items: [
          'Added Employees and Positions to Human Resources. A Position links Job Title, Department, Pay Grade, Employment Status, Job Category, Work Shift and Position Status; an Employee is linked to a User (may or may not also be a Member) and has a full position history plus Skills, Qualifications, Licenses, Memberships, Emergency Contacts, Dependents and Immigration tabs.',
          'Moved the Organization (Manager) tab from System > Users and Profile > Account to the Employee page — a manager is now another employee, not just any user.',
          'Added Departments, Skills, Licenses and Memberships to System > Lists.',
          'Added Passport No. and Passport Expiry to the Nationalities tab on System > Users and Profile > Account.',
        ],
      },
    ],
  },
  {
    version: '1.22.0',
    date: '2026-08-29',
    entries: [
      {
        title: 'System',
        items: [
          'Added Chart of Accounts, Accounting Periods and Exchange Rates pages for Finance & Accounting setup, with VNPF starter data. Exchange Rates uses VUV as the base currency and can sync live rates from a public API.',
        ],
      },
    ],
  },
  {
    version: '1.21.0',
    date: '2026-08-29',
    entries: [
      {
        title: 'System',
        items: [
          'Users and Account now have Organization (manager lookup), Nationalities and Languages tabs; removed the redundant Profile tab from Users (its fields were already on Details) and renamed Account\'s Profile tab to Details to match.',
          'Added Employment Statuses, Job Categories and Qualification Levels to System > Lists; renamed the "User Designations" list to Job Titles.',
          'Removed the Resources Administrator/Operator/Viewer roles from System > Users > Roles.',
          'Added an Organization section to Settings (registration number, tax ID, contact details, address, a separate logo) and new Pay Grades and Work Shifts pages.',
        ],
      },
    ],
  },
  {
    version: '1.20.0',
    date: '2026-08-29',
    entries: [
      {
        title: 'Investment Management',
        items: [
          'Investment Management is now an expandable menu group, split into two pages: Assets (the individual investment register) and Policy (the asset-allocation breakdown and target-range policy).',
        ],
      },
    ],
  },
  {
    version: '1.19.1',
    date: '2026-08-28',
    entries: [
      {
        title: 'Memberships',
        items: [
          'Statements is now reachable from the menu — Memberships expands into Members and Statements. Staff search for a member first; a signed-in Member goes straight to their own statement.',
        ],
      },
    ],
  },
  {
    version: '1.19.0',
    date: '2026-08-28',
    entries: [
      {
        title: 'Memberships',
        items: [
          'Added Statements to the Memberships module — Mini, Detail and Annual views of a member\'s contribution history and balance, each viewable on screen or downloadable as a PDF, reachable from a member\'s record.',
          'Added a Contributions ledger for entering, editing and bulk-importing (via CSV) a member\'s employer/employee contributions and interest credits.',
        ],
      },
    ],
  },
  {
    version: '1.18.0',
    date: '2026-08-27',
    entries: [
      {
        title: 'Memberships',
        items: [
          'A member record can no longer exist without a linked user account — this is now enforced by the database itself, not just the form.',
        ],
      },
    ],
  },
  {
    version: '1.17.0',
    date: '2026-09-09',
    entries: [
      {
        title: 'System',
        items: [
          'Temporarily hide Asset Categories, Asset Departments, Booking Status, Facility Status, Inventory Categories and Inventory Units from System > Lists, matching Resources already being hidden elsewhere in the portal.',
        ],
      },
      {
        title: 'Fixes',
        items: [
          "Account > Profile now refreshes the signed-in user's own roles when the page loads, instead of showing whatever was cached at login — a role change made elsewhere (e.g. System > Users) now shows up immediately.",
          'Human Resources and Investment Management role names now show their proper labels (e.g. "Human Resources Administrator") instead of the raw role key, in the Account page and account menu.',
        ],
      },
    ],
  },
  {
    version: '1.16.0',
    date: '2026-09-08',
    entries: [
      {
        title: 'Human Resources',
        items: [
          'Add Human Resources reports: Attendance Register, Leave Register, Payroll Register, Overtime Report, Recruitment Pipeline, Performance Reviews Register, Training Records Register, Employee Documents Register and Separation Register — each filterable with PDF export, matching every other module.',
        ],
      },
    ],
  },
  {
    version: '1.15.0',
    date: '2026-09-07',
    entries: [
      {
        title: 'Recruitment',
        items: [
          'Add offered salary and start date to a candidate’s record, and a generated Offer Letter PDF export once they reach the Offer (or Hired) stage.',
        ],
      },
    ],
  },
  {
    version: '1.14.0',
    date: '2026-09-06',
    entries: [
      {
        title: 'Investment Management',
        items: [
          'Investment records now have a dedicated detail page and a full-page add/edit form, replacing the previous inline modals — matching how Assets are viewed and edited elsewhere in the portal.',
        ],
      },
    ],
  },
  {
    version: '1.13.0',
    date: '2026-09-05',
    entries: [
      {
        title: 'Investment Management',
        items: [
          'Add Investment Management reports: an Investment Register (filterable by asset class, status and date) and an Asset Allocation breakdown, both with PDF export.',
        ],
      },
      {
        title: 'Reports',
        items: ['Temporarily hide the Resources category from the Reports menu, matching Resources already being hidden from the main navigation.'],
      },
    ],
  },
  {
    version: '1.12.0',
    date: '2026-09-04',
    entries: [
      {
        title: 'Investment Management',
        items: [
          'Add Investment Management: a register of holdings across every asset class the Fund holds (property, equity, loans, fixed interest, cash, offshore), each with an amount, acquisition/maturity dates and an expected return rate.',
          'A live asset-allocation breakdown shows the current % held in each class, checked against configurable policy ranges — a class outside its target range is flagged as a breach.',
        ],
      },
    ],
  },
  {
    version: '1.11.0',
    date: '2026-09-03',
    entries: [
      {
        title: 'Human Resources',
        items: [
          'Add Separation: HR drafts a separation record (type, effective date, optional reason and final pay amount) for an employee, then finalizes it to lock it in. The employee can then see their own finalized record; separation types are configurable under System > Lists.',
        ],
      },
    ],
  },
  {
    version: '1.10.1',
    date: '2026-09-02',
    entries: [
      {
        title: 'Fixes',
        items: [
          "The dashboard calendar's \"+N more\" is now clickable and shows every event on that day, not just the extra ones hidden from the grid.",
          'Renamed "+ Add event" and "+ Add member" buttons to "+ Add" for consistency with the rest of the portal.',
        ],
      },
    ],
  },
  {
    version: '1.10.0',
    date: '2026-09-02',
    entries: [
      {
        title: 'Human Resources',
        items: [
          'Add Documents: HR uploads a document (type, required file, optional notes) for an employee. The employee can see and download their own documents read-only; document types are configurable under System > Lists.',
        ],
      },
    ],
  },
  {
    version: '1.9.0',
    date: '2026-09-01',
    entries: [
      {
        title: 'Human Resources',
        items: [
          'Add Training: HR logs a training record for an employee (course, date, status) with an optional certificate attachment. The employee can see their own records read-only, including downloading their own certificate.',
        ],
      },
    ],
  },
  {
    version: '1.8.0',
    date: '2026-08-31',
    entries: [
      {
        title: 'Human Resources',
        items: [
          'Add Performance: HR drafts a review (period, rating, comments) for an employee, then finalizes it to lock it in. The employee can then see their own finalized reviews; rating types are configurable under System > Lists.',
          'Rename the Severance placeholder to Separation.',
        ],
      },
    ],
  },
  {
    version: '1.7.0',
    date: '2026-08-30',
    entries: [
      {
        title: 'Human Resources',
        items: [
          'Add Recruitment: manage vacancies and their candidates, moving each candidate through a configurable hiring pipeline (types under System > Lists).',
          'Attach an optional CV to a candidate and download or remove it later.',
        ],
      },
    ],
  },
  {
    version: '1.6.0',
    date: '2026-08-29',
    entries: [
      {
        title: 'Human Resources',
        items: [
          'Add PDF export to Payslips — download a finalized payslip as a PDF, with the same base salary, additions and deductions breakdown shown on screen.',
        ],
      },
    ],
  },
  {
    version: '1.5.0',
    date: '2026-08-28',
    entries: [
      {
        title: 'Human Resources',
        items: [
          'Add Overtime: submit your own overtime hours, HR approves or rejects. An hourly rate can now be set per employee in Payroll (alongside base salary).',
          'Approved overtime is automatically added to Payroll as an Overtime line (hours × hourly rate × time-and-a-half) the next time a pay run is created for that period.',
        ],
      },
    ],
  },
  {
    version: '1.4.0',
    date: '2026-08-27',
    entries: [
      {
        title: 'Human Resources',
        items: [
          'Add Payroll: create a draft pay run covering every active employee, edit base salary and add itemized additions/deductions (types configurable via System > Lists), then finalize to lock it.',
          'Add Payslips: a self-service view of your own finalized pay runs, with a breakdown of base salary, additions and deductions; HR can see and search everyone\'s.',
        ],
      },
    ],
  },
  {
    version: '1.3.0',
    date: '2026-08-26',
    entries: [
      {
        title: 'Human Resources',
        items: [
          'Add placeholder pages under Human Resources for Overtime, Payroll, Payslips, Recruitment, Performance, Training, Severance and Documents.',
        ],
      },
    ],
  },
  {
    version: '1.2.0',
    date: '2026-08-26',
    entries: [
      {
        title: 'Human Resources',
        items: [
          'Add Attendance: self-service clock in/out (multiple sessions per day supported) and a Timesheet tab for logging hours against an activity, with HR oversight (search, edit, delete) on top.',
          'Add Leave: submit a leave request with type, dates and reason; HR can approve or reject, with leave types configurable via System > Lists.',
        ],
      },
    ],
  },
  {
    version: '1.1.0',
    date: '2026-08-25',
    entries: [
      {
        title: 'Navigation & Dashboard',
        items: [
          'Move Dashboard to a top-level nav item; move Announcements and Events under System.',
          'Dashboard now shows Announcements and an event calendar.',
          'Bold Revenue and Procurement in the nav; hide Resources, Assets, Facilities, Bookings and Inventory until they\'re ready.',
          'Sort System > Lists alphabetically; remove dead settings inherited from the source app (Agent Reminders, Vouchers/Vendor Payments prefixes).',
          'Update Help & Support to reflect the current module set.',
        ],
      },
    ],
  },
  {
    version: '1.0.0',
    date: '2026-08-25',
    entries: [
      {
        title: 'Platform',
        items: [
          'Rebuilt the platform on a more complete foundation, adopting a mature authentication and permissions system (Google/Microsoft sign-in, granular roles, two-factor authentication, session management, login history) in place of the earlier bespoke version.',
        ],
      },
      {
        title: 'Finance & Accounting',
        items: [
          'Revenue: customers, quotations, invoices and receipts, with a quotation-to-invoice conversion flow and both cash and credit receipt recording.',
          'Procurement: purchase requests with a manager/board/chairman approval chain, purchase orders, goods receiving, vendor invoices, payment vouchers and payments, plus a supplier directory.',
          'Resources: an asset register, facility directory with room bookings, and stock inventory with movement tracking.',
          'A full set of Finance & Accounting reports (registers, pipelines, summaries) with PDF export.',
        ],
      },
      {
        title: 'Memberships',
        items: [
          'A real Memberships module: member directory with self-service portal accounts, replacing the placeholder page.',
          'Member directory, new-members and demographics reports.',
        ],
      },
      {
        title: 'Communications',
        items: [
          'Add Announcements and Events, with reports for each.',
        ],
      },
      {
        title: 'System',
        items: [
          'Add Message Templates for system-generated notifications.',
          'Settings, Lists, Authentication and Users all rebuilt on the new foundation, with richer session, security and audit detail on user accounts.',
        ],
      },
    ],
  },
  {
    version: '0.5.0',
    date: '2026-08-25',
    entries: [
      {
        title: 'Users',
        items: [
          'Add a Roles tab to the Add/Edit User form.',
          'Replace the row Edit button with View, Edit and Delete/Restore icon buttons; narrow the status filter dropdown.',
          'Add a User detail page (profile summary, roles).',
          'New user creation now respects System > Settings > Notifications: either set a password directly, or email the new user a link to set their own.',
        ],
      },
      {
        title: 'Settings',
        items: [
          'Add the Settings page: Branding (name, icon upload), Notifications (portal-account emails), Procurement (quotation threshold, board approvals), Receipts, Support, and Validations (mobile number pattern).',
        ],
      },
      {
        title: 'Authentication',
        items: [
          'Add the Authentication page: configure Google and Microsoft single sign-on (client ID/secret, redirect URI).',
          'Staff can now sign in with Google or Microsoft in addition to a password, matched to an existing System > Users account by email.',
        ],
      },
      {
        title: 'Lists',
        items: [
          'Add the Lists page: create and manage the option values used in dropdowns throughout the portal.',
        ],
      },
    ],
  },
  {
    version: '0.4.0',
    date: '2026-08-25',
    entries: [
      {
        title: 'Navigation',
        items: [
          'Rename Memberships (was Membership Management) and promote it out from under Investment Management, sitting on its own between Investment Management and System.',
          'Fix Investment Management staying highlighted as active while viewing Memberships.',
        ],
      },
      {
        title: 'Users',
        items: [
          'Build the Users page for real: searchable, filterable, paginated list; create and edit forms with role assignment; activate/deactivate.',
          'New backend endpoints: GET /roles, GET /users/{id}, and search/status filtering on GET /users.',
        ],
      },
      {
        title: 'Account',
        items: [
          'Show all six of Falah\'s Account tabs (Profile, Address, Password, Security, Notifications, Login History) for visual parity — only Profile is backed by data so far; the rest say so honestly rather than presenting a form that submits to nothing.',
        ],
      },
    ],
  },
  {
    version: '0.3.0',
    date: '2026-08-25',
    entries: [
      {
        title: 'Navigation',
        items: [
          'Promote Membership Management to a top-level item, between Investment Management and System.',
        ],
      },
      {
        title: 'Help, Support & Changelog',
        items: [
          'Rebuild the Help page as a module-by-module feature guide.',
          'Rebuild the Support page as linked cards to Help and the Change Log.',
          'Group changelog entries by titled sub-entry instead of one flat list per version.',
        ],
      },
    ],
  },
  {
    version: '0.2.0',
    date: '2026-08-25',
    entries: [
      {
        title: 'Navigation',
        items: [
          'Nest Membership Management under Investment Management in the sidebar.',
          'Add Settings, Lists and Authentication under the System menu.',
          'Add Account, Help and Support to the profile menu, with working pages.',
        ],
      },
    ],
  },
  {
    version: '0.1.0',
    date: '2026-08-25',
    entries: [
      {
        title: 'Platform',
        items: [
          'Add a version number and in-app changelog page, linked from the footer.',
        ],
      },
    ],
  },
];

export const CURRENT_VERSION = CHANGELOG[0].version;
